import TeamMemberRoles from "backend-models/memberRoles";
import FullHeightForm from "components/FullHeightForm";
import { Button, Drawer, Form, Input, Select, Spin } from "components/styleguide";
import { ForumTeamMemberRequestModel, UserResponseModel } from "generated/api";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";

const { Option } = Select;

const AddTeamMemberDrawer = ({
  isVisible,
  isLoading,
  onClose,
  onFinish,
  users,
  isUsersLoading,
}: {
  isVisible: boolean;
  isLoading: boolean;
  onClose: () => void;
  onFinish: (values: ForumTeamMemberRequestModel) => void;
  users: UserResponseModel[] | undefined;
  isUsersLoading: boolean;
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [userId, setUserId] = useState(null);

  const handleSelect = (record: string) => {
    if (record) {
      const { jobTitle, name, id, mobilePhone, businessPhone } = JSON.parse(record);

      form.setFieldsValue({ jobTitle, name, mobilePhone, businessPhone });

      setUserId(id);
    }
  };

  return (
    <Drawer
      className="team-members-drawer"
      title={"Add Team Member"}
      placement="right"
      onClose={onClose}
      open={isVisible}
      contentWrapperStyle={{ minWidth: "50%" }}
      destroyOnClose
    >
      {isUsersLoading ? (
        <div className="loader">
          <Spin />
        </div>
      ) : (
        <FullHeightForm
          id="teamMembersForm"
          name="team-members-form"
          labelCol={{ span: 4 }}
          form={form}
          preserve={false}
          actionsPrepend={<Button onClick={onClose}>{t("cancel")}</Button>}
          actionsAppend={
            <Button type="primary" htmlType="submit" loading={isLoading}>
              {t("save")}
            </Button>
          }
          onFinish={(values) => onFinish({ ...values, userId })}
        >
          <Form.Item
            label={t("name")}
            name="name"
            rules={[
              {
                required: true,
                message: t("errors.required", { prop: "$t(name)" }),
              },
            ]}
          >
            <Select showSearch optionFilterProp="children" onSelect={handleSelect}>
              {users?.map(({ name, jobTitle, id, mobilePhone, businessPhone }, index) => (
                <Option key={index} value={JSON.stringify({ name, jobTitle, id, mobilePhone, businessPhone })}>
                  {name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("jobTitle")}
            name="jobTitle"
            rules={[
              {
                max: 200,
                message: t("'Job Title' field has a maximum of 200 characters"),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("role")}
            name="role"
            rules={[
              {
                required: true,
                message: t("errors.required", { prop: "$t(role)" }),
              },
            ]}
          >
            <Select>
              {TeamMemberRoles.LIST.map(({ value, label }) => (
                <Option key={value} value={label}>
                  {t(`jobRoles.${label}`)}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            label={t("Mobile phone")}
            name="mobilePhone"
            rules={[
              {
                max: 32,
                message: t("The character limit is {{count}}", { count: 32 }),
              },
            ]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            label={t("Phone Number")}
            name="businessPhone"
            rules={[
              {
                max: 32,
                message: t("The character limit is {{count}}", { count: 32 }),
              },
            ]}
          >
            <Input />
          </Form.Item>
        </FullHeightForm>
      )}
    </Drawer>
  );
};

export default AddTeamMemberDrawer;
